import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useGraphEditorStore } from '@/graph-store';
import { isLatestMaterial } from '@/graph-store/actions/transferHelpers';

export interface SelectDestinationModalProps {
  sourceMaterialId: string;
  excludedMaterialIds: string[];
  onSelectExisting: (materialId: string) => void;
  onCreateNew: () => void;
  onClose: () => void;
}

export function SelectDestinationModal({
  sourceMaterialId,
  excludedMaterialIds,
  onSelectExisting,
  onCreateNew,
  onClose,
}: SelectDestinationModalProps) {
  const materialNodes = useGraphEditorStore((s) => s.graphData.materialNodes);
  const flowEdges = useGraphEditorStore((s) => s.graphData.flowEdges);
  const operationNodes = useGraphEditorStore((s) => s.graphData.operationNodes);

  const [selected, setSelected] = useState<string | null>(null);

  const excludeSet = useMemo(
    () => new Set([sourceMaterialId, ...excludedMaterialIds]),
    [sourceMaterialId, excludedMaterialIds],
  );

  const candidates = useMemo(() => {
    return Object.entries(materialNodes)
      .filter(
        ([id]) =>
          !excludeSet.has(id) && isLatestMaterial(id, flowEdges),
      )
      .map(([id, spec]) => {
        // Find the operation that produced this material (if any)
        const producingEdge = Object.values(flowEdges).find(
          (e) => e.targetId === id,
        );
        let producingOp: string | null = null;
        if (producingEdge) {
          const op = operationNodes[producingEdge.sourceId];
          if (op) producingOp = op.displayName;
        }
        return { id, spec, producingOp };
      });
  }, [materialNodes, flowEdges, operationNodes, excludeSet]);

  const handleConfirm = () => {
    if (selected) {
      onSelectExisting(selected);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="select-destination-modal"
    >
      <DialogTitle>Select Destination</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {candidates.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No available destinations. Use "New Destination" to create one.
            </Typography>
          </Box>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Container</TableCell>
                <TableCell>Material</TableCell>
                <TableCell>Step details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {candidates.map(({ id, spec, producingOp }) => (
                <TableRow
                  key={id}
                  hover
                  selected={selected === id}
                  onClick={() => setSelected(id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox">
                    <Radio checked={selected === id} size="small" />
                  </TableCell>
                  <TableCell>{spec.container?.name ?? '—'}</TableCell>
                  <TableCell>
                    {spec.name}
                    {spec.constituents?.length
                      ? ` (${spec.constituents
                          .slice(0, 3)
                          .map((c) => c.name)
                          .join(', ')}${spec.constituents.length > 3 ? ', ...' : ''})`
                      : ''}
                  </TableCell>
                  <TableCell>{producingOp ?? '(origin)'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCreateNew}>New Destination</Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!selected}
          onClick={handleConfirm}
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
}
